import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { answerService } from '../../services/api';

// État initial
const initialState = {
  answers: [],
  currentAnswer: null,
  loading: false,
  error: null,
  total: 0
};

// Actions asynchrones
export const fetchAnswers = createAsyncThunk(
  'answers/fetchAnswers',
  async (_, { rejectWithValue }) => {
    try {
      const response = await answerService.getAll();
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchAnswerById = createAsyncThunk(
  'answers/fetchAnswerById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await answerService.getById(id);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const createAnswer = createAsyncThunk(
  'answers/createAnswer',
  async (answerData, { rejectWithValue }) => {
    try {
      const response = await answerService.create(answerData);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateAnswer = createAsyncThunk(
  'answers/updateAnswer',
  async ({ id, answerData }, { rejectWithValue }) => {
    try {
      const response = await answerService.update(id, answerData);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteAnswer = createAsyncThunk(
  'answers/deleteAnswer',
  async (id, { rejectWithValue }) => {
    try {
      await answerService.delete(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Slice
const answerSlice = createSlice({
  name: 'answer',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentAnswer: (state, action) => {
      state.currentAnswer = action.payload;
    },
    clearCurrentAnswer: (state) => {
      state.currentAnswer = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch all answers
      .addCase(fetchAnswers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAnswers.fulfilled, (state, action) => {
        state.loading = false;
        state.answers = action.payload.data || action.payload;
        state.total = action.payload.total || state.answers.length;
        state.error = null;
      })
      .addCase(fetchAnswers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch answer by ID
      .addCase(fetchAnswerById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAnswerById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentAnswer = action.payload;
        state.error = null;
      })
      .addCase(fetchAnswerById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Create answer
      .addCase(createAnswer.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createAnswer.fulfilled, (state, action) => {
        state.loading = false;
        state.answers.push(action.payload);
        state.error = null;
      })
      .addCase(createAnswer.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update answer
      .addCase(updateAnswer.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateAnswer.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.answers.findIndex(answer => answer.id === action.payload.id);
        if (index !== -1) {
          state.answers[index] = action.payload;
        }
        if (state.currentAnswer && state.currentAnswer.id === action.payload.id) {
          state.currentAnswer = action.payload;
        }
        state.error = null;
      })
      .addCase(updateAnswer.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Delete answer
      .addCase(deleteAnswer.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteAnswer.fulfilled, (state, action) => {
        state.loading = false;
        state.answers = state.answers.filter(answer => answer.id !== action.payload);
        if (state.currentAnswer && state.currentAnswer.id === action.payload) {
          state.currentAnswer = null;
        }
        state.error = null;
      })
      .addCase(deleteAnswer.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const { clearError, setCurrentAnswer, clearCurrentAnswer } = answerSlice.actions;

export default answerSlice.reducer;
